"use client";

import { useState, useCallback, useEffect } from "react";
import { ArrowLeft, CheckCircle2, XCircle, Mic, Square, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { SpeakerButton } from "@/components/speaker-button";
import { useTTS } from "@/lib/use-tts";
import { useSpeechToText } from "@/lib/use-speech-to-text";
import { useTranslations } from "@/lib/use-translations";
import { useLanguage } from "@/lib/language-context";
import { matchSpokenWord } from "@/lib/spelling-comparison";
import type { TopicVocabulary } from "@/lib/dialog-data";
import type { LocaleCode } from "@/lib/api/client";

type SpellingState = "show" | "recording" | "processing" | "result_pass" | "result_fail" | "complete";

interface SpellingPracticeProps {
  vocabulary: TopicVocabulary;
  language?: LocaleCode;
  onBack: () => void;
}

export function SpellingPractice({ vocabulary, onBack }: SpellingPracticeProps) {
  const words = vocabulary.words;
  const [currentIndex, setCurrentIndex] = useState(0);
  const [state, setState] = useState<SpellingState>("show");
  const [heardText, setHeardText] = useState("");
  const [correctCount, setCorrectCount] = useState(0);
  const t = useTranslations();
  const { targetLanguage } = useLanguage();
  const langCode = targetLanguage.code;
  const tts = useTTS({ lang: langCode });

  const currentWord = words[currentIndex] as typeof words[number] | undefined;

  const handleSTTResult = useCallback(
    (transcript: string) => {
      setHeardText(transcript);
      if (currentWord && matchSpokenWord(currentWord.baseForm, transcript)) {
        setCorrectCount((c) => c + 1);
        setState("result_pass");
      } else {
        setState("result_fail");
      }
    },
    [currentWord]
  );

  const stt = useSpeechToText({
    language: langCode,
    onResult: handleSTTResult,
  });

  // Recover from STT errors (processing done but no result)
  useEffect(() => {
    if (state === "processing" && !stt.isProcessing && !stt.isRecording) {
      setState("show");
    }
  }, [state, stt.isProcessing, stt.isRecording]);

  const handleNext = () => {
    if (currentIndex + 1 >= words.length) {
      setState("complete");
    } else {
      setCurrentIndex((i) => i + 1);
      setState("show");
      setHeardText("");
    }
  };

  const handleTryAgain = () => {
    setState("show");
    setHeardText("");
  };

  const handleSkip = () => {
    handleNext();
  };

  const handleRestart = () => {
    setCurrentIndex(0);
    setCorrectCount(0);
    setState("show");
    setHeardText("");
  };

  const handleToggleRecording = () => {
    if (stt.isRecording) {
      setState("processing");
      stt.stopRecording();
    } else {
      setState("recording");
      stt.startRecording();
    }
  };

  if (words.length === 0) {
    return (
      <div className="p-4 text-center text-muted-foreground">
        No words to practice
      </div>
    );
  }

  // Complete screen
  if (state === "complete") {
    return (
      <div className="p-4 space-y-6 max-w-lg mx-auto">
        <Button variant="ghost" size="icon" onClick={onBack} className="rounded-xl">
          <ArrowLeft className="w-5 h-5" />
        </Button>

        <div className="text-center space-y-4 pt-8">
          <CheckCircle2 className="w-16 h-16 text-emerald-500 mx-auto" />
          <h2 className="text-2xl font-bold">{t.learning.practiceComplete || "Practice Complete!"}</h2>
          <p className="text-lg text-muted-foreground">
            {(t.learning.wordsCorrectCount || "{correct} of {total} correct")
              .replace("{correct}", String(correctCount))
              .replace("{total}", String(words.length))}
          </p>

          <div className="flex gap-3 justify-center pt-4">
            <Button variant="outline" onClick={handleRestart}>
              {t.learning.practiceAgain || "Practice Again"}
            </Button>
            <Button onClick={onBack}>
              {t.learning.done || "Done"}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  if (!currentWord) return null;

  return (
    <div className="p-4 space-y-6 max-w-lg mx-auto">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="icon" onClick={onBack} className="rounded-xl">
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div className="flex-1">
          <h3 className="text-sm font-medium text-muted-foreground">
            {t.learning.practiceSpelling || "Spelling"}
          </h3>
          <div className="text-xs text-muted-foreground">
            {currentIndex + 1} / {words.length}
          </div>
        </div>
      </div>

      {/* Progress bar */}
      <div className="w-full h-1.5 bg-muted rounded-full overflow-hidden">
        <div
          className="h-full bg-emerald-500 rounded-full transition-all duration-300"
          style={{ width: `${((currentIndex) / words.length) * 100}%` }}
        />
      </div>

      {/* Word display */}
      <div className="text-center space-y-3 py-8">
        <div className="text-3xl font-bold text-foreground">
          {currentWord.baseForm}
        </div>
        {currentWord.vocalizationTip && (
          <div className="text-lg text-muted-foreground">
            {currentWord.vocalizationTip}
          </div>
        )}
        <div className="flex justify-center">
          <SpeakerButton
            state={tts.state}
            onClick={() => tts.speak(currentWord.baseForm)}
            size="lg"
          />
        </div>
      </div>

      {/* Result feedback */}
      {state === "result_pass" && (
        <div className="text-center space-y-4 animate-fade-up">
          <CheckCircle2 className="w-12 h-12 text-emerald-500 mx-auto" />
          <p className="text-lg font-medium text-emerald-600 dark:text-emerald-400">
            {t.learning.correct || "Correct!"}
          </p>
          <Button onClick={handleNext} className="w-full">
            {t.learning.nextWord || "Next"}
          </Button>
        </div>
      )}

      {state === "result_fail" && (
        <div className="text-center space-y-4 animate-fade-up">
          <XCircle className="w-12 h-12 text-red-500 mx-auto" />
          {heardText && (
            <p className="text-sm text-muted-foreground">
              {t.learning.weHeard || "We heard:"} <span className="font-medium text-foreground">{heardText}</span>
            </p>
          )}
          <div className="flex gap-3 justify-center">
            <Button variant="outline" onClick={handleTryAgain}>
              {t.learning.tryAgain || "Try Again"}
            </Button>
            <Button variant="ghost" onClick={handleSkip}>
              {t.learning.skipWord || "Skip"}
            </Button>
          </div>
        </div>
      )}

      {/* Mic button */}
      {(state === "show" || state === "recording" || state === "processing") && (
        <div className="text-center space-y-3">
          <button
            onClick={handleToggleRecording}
            disabled={state === "processing"}
            className={`
              w-20 h-20 rounded-full mx-auto flex items-center justify-center transition-all shadow-lg
              ${state === "recording"
                ? "bg-destructive text-destructive-foreground scale-110"
                : state === "processing"
                  ? "bg-muted text-muted-foreground cursor-wait"
                  : "bg-emerald-500 text-white hover:scale-105 active:scale-95"
              }
            `}
          >
            {state === "processing" ? (
              <Loader2 className="w-8 h-8 animate-spin" />
            ) : state === "recording" ? (
              <Square className="w-8 h-8 fill-current" />
            ) : (
              <Mic className="w-8 h-8" />
            )}
          </button>

          {state === "recording" && (
            <div className="space-y-1">
              <p className="text-base font-medium text-foreground">
                {t.generator?.tapToStop || "Tap to stop"}
              </p>
              <div className="flex items-center justify-center gap-2 text-destructive animate-pulse">
                <div className="w-2 h-2 rounded-full bg-destructive animate-ping" />
                <span className="text-sm font-medium">{t.generator?.recording || "Recording..."}</span>
              </div>
            </div>
          )}

          {state === "processing" && (
            <p className="text-sm text-muted-foreground">
              {t.learning.transcribing || "Transcribing..."}
            </p>
          )}

          {state === "show" && (
            <p className="text-sm text-muted-foreground">
              {t.generator?.tapAndTalk || "Tap and talk"}
            </p>
          )}

          {stt.error && state === "show" && (
            <p className="text-xs text-destructive">{stt.error}</p>
          )}
        </div>
      )}
    </div>
  );
}
